import { baseurl, LogErrorMessage, post } from '../service';
import { isValidNpi } from '../validation';

/**
 * @typedef {import('../../types/provider').Provider} Provider
 */

/**
 * Request payload for POST /SelfService/verifyProvider
 * @typedef {Object} VerifyProviderRequest
 * @property {string} pwid - Provider's PWID
 * @property {string} deaNumber - Last 4 characters of DEA/License
 * @property {string} npi - NPI number (can be empty string)
 */

/**
 * Response from POST /SelfService/verifyProvider
 * @typedef {Object} VerifyProviderResponse
 * @property {boolean} success - Whether verification was successful
 * @property {string} message - Response message
 */

/**
 * Request payload for POST /search/providers
 * @typedef {Object} SearchProvidersRequest
 * @property {'npi'|'pwid'|'name'} SearchType - Type of search
 * @property {string} SearchValue - Value to search for
 * @property {string} [SearchLocation] - City filter for name search (e.g., "Denver")
 * @property {number} [Page] - Page number, 1-based (default: 1)
 * @property {number} [PageSize] - Number of results per page (default: 30)
 * @property {string} [SortBy] - Field to sort by (default: 'Name')
 * @property {string} [SortOrder] - Sort order 'asc' or 'desc' (default: 'asc')
 */

/**
 * Success response from POST /search/providers
 * @typedef {Object} SearchProvidersSuccessResponse
 * @property {true} success - Operation was successful
 * @property {null} errorMessage - null when successful
 * @property {null} errorCode - null when successful
 * @property {number} totalRecords - Total number of matching providers
 * @property {Provider[]} results - Array of provider results
 *
 * Error response from POST /search/providers
 * @typedef {Object} SearchProvidersErrorResponse
 * @property {false} success - Operation failed
 * @property {string} errorMessage - Error message for search failure
 * @property {string} errorCode - Error code for search failure
 * @property {0} totalRecords - Always 0 for errors
 * @property {[]} results - Always empty array for errors
 *
 * Response from POST /search/providers (discriminated union)
 * @typedef {SearchProvidersSuccessResponse | SearchProvidersErrorResponse} SearchProvidersResponse
 */

/**
 * @typedef {Object} FetchFirstProviderResponse
 * @property {Provider|null} provider - First provider match (if any)
 * @property {SearchProvidersErrorResponse['errorCode']|null} errorCode - Error code for search failure
 * @property {SearchProvidersErrorResponse['errorMessage']|null} errorMessage - Error message for search failure
 */

const DEFAULT_SEARCH_PROVIDERS_PAYLOAD = {
  Page: 1, // Backend uses 1-based pagination
  PageSize: 30,
  SortBy: 'Name',
  SortOrder: 'asc',
};

/**
 * Search for provider using the admin search API
 * @param {'npi'|'pwid'|'name'} searchType - Type of search
 * @param {string} searchValue - Value to search for (NPI number, PWID, or name)
 * @param {string} [searchLocation] - Optional city filter for name search (e.g., "Denver")
 * @returns {Promise<SearchProvidersResponse>} Full search response or null if error
 */
const searchProviders = async (searchType, searchValue, searchLocation) => {
  if (!searchValue) {
    console.error(`searchProvider: ${searchType} value is required`);
    /** @type {SearchProvidersErrorResponse} */
    const errorResponse = {
      success: false,
      errorCode: 'MISSING_SEARCH_VALUE',
      errorMessage: `searchProviders: ${searchType} value is required`,
      totalRecords: 0,
      results: [],
    };
    return errorResponse;
  }

  try {
    const url = `${baseurl()}/search/providers`;
    /** @type {SearchProvidersRequest} */
    const payload = {
      ...DEFAULT_SEARCH_PROVIDERS_PAYLOAD,
      SearchType: searchType,
      SearchValue: searchValue,
      ...(searchLocation ? { SearchLocation: searchLocation } : {}),
    };

    /** @type {SearchProvidersResponse} */
    const response = await post(url, payload, '2.0');
    if (!response.success) {
      response.errorCode = response.errorCode || 'UNKNOWN_ERROR';
    }
    return response;
  } catch (error) {
    console.error(`${searchType.toUpperCase()} search failed:`, error);
    LogErrorMessage(error);

    /** @type {SearchProvidersErrorResponse} */
    const errorResponse = {
      success: false,
      errorCode: error?.errorCode || 'UNKNOWN_ERROR',
      errorMessage:
        error?.errorMessage || `searchProviders: ${searchType} failed`,
      totalRecords: 0,
      results: [],
    };

    return errorResponse;
  }
};

/**
 * Fetch first provider matching the NPI
 * @param {string} npiNumber - 10-digit NPI number
 * @returns {Promise<FetchFirstProviderResponse>} First provider result or null if not found
 */
export const fetchFirstProviderByNpi = async (npiNumber) => {
  if (!npiNumber || !isValidNpi(npiNumber)) {
    console.error('fetchFirstProviderByNpi: NPI must be a 10-digit number');
    return {
      provider: null,
      errorCode: 'INVALID_NPI',
      errorMessage: 'NPI must be a 10-digit number',
    };
  }

  const response = await searchProviders('npi', npiNumber);

  if (!response.success) {
    return {
      provider: null,
      errorCode: response.errorCode || 'UNKNOWN_ERROR',
      errorMessage: response.errorMessage || 'Search failed',
    };
  }

  return {
    provider: response.results?.[0] || null,
    errorCode: null,
    errorMessage: null,
  };
};

/**
 * Search for providers by name with optional location filter
 * @param {string} name - Provider name to search for
 * @param {string} [location] - Optional city filter (e.g., "Denver")
 * @returns {Promise<SearchProvidersResponse>} Search response with matching providers
 */
export const searchProvidersByName = async (name, location) => {
  const normalizedName = (name || '').trim();

  if (!normalizedName || normalizedName.length < 2) {
    /** @type {SearchProvidersSuccessResponse} */
    const emptyResponse = {
      success: true,
      errorMessage: null,
      errorCode: null,
      totalRecords: 0,
      results: [],
    };
    return emptyResponse;
  }

  return searchProviders('name', normalizedName, location);
};

/**
 * Verify provider using PWID and last 4 digits (DEA/license)
 * @param {string} pwid - Provider's PWID
 * @param {string} last4 - Last 4 characters to verify
 * @returns {Promise<VerifyProviderResponse>} Verification result
 */
export const verifyProviderByLast4 = async (pwid, last4) => {
  if (!pwid || !last4) {
    return {
      success: false,
      message: 'PWID and 4-digit last4 are required',
    };
  }

  try {
    const url = `${baseurl()}/SelfService/verifyProvider`;
    /** @type {VerifyProviderRequest} */
    const payload = {
      pwid,
      deaNumber: last4,
      npi: '',
    };

    /** @type {VerifyProviderResponse} */
    const response = await post(url, payload);
    return response || { success: false, message: 'No response from server' };
  } catch (error) {
    LogErrorMessage(error);
    return { success: false, message: 'Verification failed' };
  }
};
